import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import { format } from 'date-fns'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as organizations from '../../organizations'
import * as roomTypes from '../../room-types'
import * as userAccess from '../../user-access'
import * as clusters from '../clusters'

import * as gql from './gql'
import type { TypeParams } from './types'

/**
 * NOTE: This constant represents the default text to be shown
 * in table cells when a cell contains multiple values.
 */
export const DETAIL_TEXT = 'Open for details'

/** Quantity of records to fetch in one chunk */
const SPACE_RECORD_CHUNK_QUANTITY = 1000

/** Space record API, for fetching and updating space records */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(organizations.Feature)

  /** @see {@link roomTypes.Collection} */
  private readonly _roomTypesEc = ngCore.inject(roomTypes.Collection)

  /** @see {@link userAccess.users.Collection} */
  private readonly _usersEc = ngCore.inject(userAccess.users.Collection)

  /** @see {@link clusters.Collection} */
  private readonly _clustersEc = ngCore.inject(clusters.Collection)

  /** @see {@link dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(dms.schema.Feature)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.everything<TypeParams>(
    (context, request) => {
      if (!context) {
        return rxjs.of([])
      }

      // Utilize pagination if it's available
      if (request?.pagination) {
        return gql.queries.spaceRecords.executor({
          ...context,
          filter: gql.inputs.filter.TRANSFORMER.getGqlFrom(
            request?.filter?.additionalFilters,
          ),
          pagination: {
            quantity: request.pagination.quantity,
            skip: request.pagination.skip,
          },
          sort: request?.sort && {
            by: request.sort.by,
            descending: request.sort.descending,
          },
        })
      } else {
        // If pagination is not available, fetch all records in smaller chunks
        return this._fetchSpaceRecordsInChunks(context, request)
      }
    },
  )

  /** @inheritdoc */
  // NOTE: This method will be used by the org assignment refresh effect
  // once the get.request action dispatch issue is resolved
  public get: ec.models.Api<TypeParams>['get'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)

    return gql.queries.spaceRecord.executor({
      ...context,
      spaceRecordId: id,
    })
  }

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    TypeAsserter.assertIsNotNullish(context)
    return gql.mutations.updateSpaceRecord.executor({
      ...context,
      spaceRecordId: id,
      update: gql.inputs.recordUpdates.TRANSFORMER.getGqlFrom(updates),
    })
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] =
    context => {
      if (!context) {
        return rxjs.of([])
      }

      return rxjs
        .combineLatest({
          clustersLookup:
            this._clustersEc.plugins.cachePlugin.feature.selectors.lookup
              .asObservable,
          schema: this._schemaFeature.selectors.schema.asObservable,
          spaceOrganizationsLookup:
            this._organizationsFeature.selectors.departmentEc.asObservable.pipe(
              rxjs.switchMap(
                departmentEc =>
                  departmentEc?.plugins.cachePlugin.feature.selectors.lookup
                    .asObservable ?? rxjs.of(undefined),
              ),
            ),
          spaceUsersLookup:
            this._usersEc.plugins.cachePlugin.feature.selectors.lookup
              .asObservable,
          spaceRoomTypesLookup:
            this._roomTypesEc.plugins.cachePlugin.feature.selectors.lookup
              .asObservable,
        })
        .pipe(
          rxjs.filter(
            ({ schema }) => !!schema?.tryGetLeafEntityOf('dim::!space'),
          ),
          rxjs.map(
            ({
              clustersLookup,
              spaceOrganizationsLookup,
              spaceRoomTypesLookup,
              schema,
              spaceUsersLookup,
            }) => {
              const spaceLeafEntity = schema.getLeafEntityOf('dim::!space')
              const ancestors = schema.getAncestorsOf(spaceLeafEntity.id)

              const ancestorAttributes: ec.models.Attribute<TypeParams>[] =
                ancestors.map(({ name, id }) => ({
                  getValueFrom: record => record.ancestors[id]?.name,
                  id,
                  isSortable: true,
                  name,
                }))

              const leafOrgEntity = schema.tryGetLeafEntityOf(
                organizations.constants.DIMENSION_ID,
              )

              return [
                ...ancestorAttributes,
                ec.models.Attribute.fromKey<TypeParams>('name', {
                  isSortable: true,
                  name: 'Room',
                }),
                {
                  getValueFrom: record => Math.round(record.sqft),
                  id: 'sqft',
                  isSortable: true,
                  name: 'Area (sqft)',
                },
                {
                  getValueFrom: record =>
                    spaceRoomTypesLookup[record.roomTypeId]?.name,
                  id: 'roomType',
                  isSortable: true,
                  name: 'Room Type',
                },
                {
                  getValueFrom: record =>
                    record.occupierId !== undefined
                      ? spaceOrganizationsLookup?.[record.occupierId]?.name
                      : undefined,
                  id: 'occupier',
                  isSortable: true,
                  name: leafOrgEntity?.name ?? 'Occupier',
                },
                {
                  getValueFrom: record =>
                    record.clusterId !== undefined
                      ? clustersLookup[record.clusterId]?.name
                      : undefined,
                  id: 'cluster',
                  isSortable: true,
                  name: 'Cluster',
                },
                ec.models.Attribute.fromKey<TypeParams>('surveyStatus', {
                  isSortable: true,
                  name: 'Status',
                }),
                {
                  getValueFrom: ({ lastUpdated }) => {
                    if (!lastUpdated) {
                      return undefined
                    }
                    const displayName =
                      spaceUsersLookup[lastUpdated.userId]?.name ??
                      'Unknown User'
                    return `${format(lastUpdated.timestamp, 'MM/dd/yyyy @ HH:mm')} by ${displayName}`
                  },
                  id: 'lastUpdated',
                  isSortable: true,
                  name: 'Last Update',
                },
              ]
            },
          ),
        )
    }

  /** @inheritdoc */
  public count: ec.models.Api<TypeParams>['count'] = (context, request) =>
    context
      ? gql.queries.recordCount.executor({
          ...context,
          filter: gql.inputs.filter.TRANSFORMER.getGqlFrom(
            request?.filter?.additionalFilters,
          ),
        })
      : rxjs.of(0)

  /**
   * Fetch all space records in chunks.
   *
   * Chunk size is defined by {@link SPACE_RECORD_CHUNK_QUANTITY}
   */
  private _fetchSpaceRecordsInChunks(
    context: TypeParams['context'],
    request: ec.models.QueryRequest<TypeParams>,
    offset = 0,
    fetchedRecords: ents.extra.space.Record[] = [],
  ): rxjs.Observable<ents.extra.space.Record[]> {
    if (!context) {
      return rxjs.of(fetchedRecords)
    }

    return gql.queries.spaceRecords
      .executor({
        ...context,
        filter: gql.inputs.filter.TRANSFORMER.getGqlFrom(
          request?.filter?.additionalFilters,
        ),
        pagination: {
          quantity: SPACE_RECORD_CHUNK_QUANTITY,
          skip: offset,
        },
        sort: request?.sort && {
          by: request.sort.by,
          descending: request.sort.descending,
        },
      })
      .pipe(
        rxjs.switchMap(recordsResult => {
          fetchedRecords = fetchedRecords.concat(recordsResult)
          if (recordsResult.length === SPACE_RECORD_CHUNK_QUANTITY) {
            return this._fetchSpaceRecordsInChunks(
              context,
              request,
              offset + SPACE_RECORD_CHUNK_QUANTITY,
              fetchedRecords,
            )
          } else {
            return rxjs.of(fetchedRecords)
          }
        }),
        rxjs.first(),
      )
  }
}
